import { useState, type ReactNode } from 'react';
import { hasTermsAcceptance } from '@steward-app/steward-types';
import { Modal } from '../components/folders/Modal';
import { useAuth } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { TermsAcceptanceLine } from './TermsAcceptanceLine';

const cls = {
  body: 'text-[13px] leading-relaxed text-body font-sans m-0',
  error: 'mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans',
  actions: 'mt-5 flex justify-end gap-2',
  ghost:   'px-4 py-2 text-[11px] font-semibold rounded-token border border-line-strong cursor-pointer bg-transparent text-navy uppercase tracking-[0.08em] font-sans',
  primary: 'px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed',
};

/**
 * The one-time Terms prompt for a person who is already in a workspace but has no
 * acceptance of the current version on record (steward-app#2630): everyone who joined before
 * the record existed, and everyone after a `TERMS_VERSION` bump.
 *
 * A person with NO membership is not prompted here. They are on their way into a first
 * workspace (onboarding, an invitation, a sponsored setup), and the button that takes them in
 * carries the same line and records the acceptance on its click.
 *
 * Shown over the app, not instead of it, and with no way to close it except agreeing or
 * signing out. Only a `/me` that ANSWERED is read: a failed one shows nothing, rather than
 * asking someone whose record could not be read.
 */
export function TermsGate({ children }: { children: ReactNode }) {
  const { memberships, meSucceeded, profile } = useWorkspace();
  const needed = meSucceeded && memberships.length > 0 && !hasTermsAcceptance(profile?.preferences as Record<string, unknown> | undefined);
  return (
    <>
      {children}
      {needed && <TermsPrompt />}
    </>
  );
}

function TermsPrompt() {
  const { acceptTerms } = useWorkspace();
  const { signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function accept() {
    setBusy(true); setErr('');
    try {
      await acceptTerms();
    } catch {
      // A fixed sentence, never the server's own text (steward-app#2047).
      setErr('We could not record your acceptance. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal titleText="Before you continue" onClose={() => {}} dismissable={false} maxWidthClassName="max-w-[480px]">
      <p className={cls.body}>
        Please take a moment to review the Steward Terms of Service and Privacy Policy.
      </p>
      <TermsAcceptanceLine className="mt-4 mb-0" />
      {err && <div className={cls.error} role="alert">{err}</div>}
      <div className={cls.actions}>
        <button type="button" onClick={() => void signOut()} disabled={busy} className={cls.ghost}>Sign out</button>
        <button type="button" onClick={() => void accept()} disabled={busy} className={cls.primary}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </Modal>
  );
}
